import { toast } from '../components/Toast';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { VatContext } from '../context/VatContext';
import { resolveAssetUrl } from '../config';
import BulkInquiryModal from '../components/BulkInquiryModal';
import CompatibleAccessories from '../components/CompatibleAccessories';
import CartGiftProgress from '../components/CartGiftProgress';
import GiftSelectionModal from '../components/GiftSelectionModal';
import CouponInput from '../components/CouponInput';
import { getCartRecommendations } from '../api/product.api';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

/** Cart APIs key off variantId — normalize ObjectId / populated shapes. */
const variantKey = (id) => String(id?._id || id || '');

export default function Cart() {
    const navigate = useNavigate();
    const { cart, cartLoading, updateQuantity, removeItem, clearCartItems, addToCart, refreshCart } = useContext(CartContext);
    const { isAuthenticated } = useContext(AuthContext);
    const { vatRate } = useContext(VatContext);

    const [removingItems, setRemovingItems] = useState({});
    const [updatingItems, setUpdatingItems] = useState({});
    const [clearing, setClearing] = useState(false);
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [recommendations, setRecommendations] = useState([]);
    const [giftModalSlab, setGiftModalSlab] = useState(null);

    useEffect(() => {
        document.title = 'Shopping Cart | Fone Express';
    }, []);

    // Fetch compatible accessories for the products currently in the cart.
    const cartProductIds = (cart?.items || [])
        .map((item) => item.productId)
        .filter(Boolean);
    const cartProductKey = cartProductIds.join(',');

    useEffect(() => {
        if (!cartProductKey) {
            setRecommendations([]);
            return;
        }
        let cancelled = false;
        console.log("cartProductKey", cartProductKey);
        getCartRecommendations(cartProductKey.split(','), 12)
            .then((res) => {
                if (!cancelled && res?.data?.isOk) {
                    setRecommendations(res.data.data || []);
                }
            })
            .catch((err) => console.error('Error fetching cart recommendations:', err));
        return () => {
            cancelled = true;
        };
    }, [cartProductKey]);

    const getProductImage = (item) => {
        const img =
            item.variant?.image ||
            item.variant?.variantImage?.find((i) => i.isPrimary)?.path ||
            item.variant?.variantImage?.[0]?.path;
        return resolveAssetUrl(img);
    };

    const handleUpdateQuantity = async (variantId, newQty) => {
        const id = variantKey(variantId);
        if (!id || updatingItems[id]) return;
        setUpdatingItems(prev => ({ ...prev, [id]: true }));
        try {
            await updateQuantity(id, newQty);
        } catch (err) {
            toast.error('Failed to update quantity');
        } finally {
            setUpdatingItems(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleRemoveItem = async (variantId) => {
        const id = variantKey(variantId);
        if (!id || removingItems[id]) return;
        setRemovingItems(prev => ({ ...prev, [id]: true }));
        try {
            await removeItem(id);
            toast.success('Item removed from cart');
        } catch (err) {
            // Sync UI if server cart already dropped the line (stale client state)
            try {
                await refreshCart();
            } catch {
                /* ignore */
            }
            toast.error('Failed to remove item');
        } finally {
            setRemovingItems(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleClearCart = async () => {
        if (clearing) return;
        setClearing(true);
        try {
            await clearCartItems();
            toast.success('Cart cleared');
        } catch (err) {
            toast.error('Failed to clear cart');
        } finally {
            setClearing(false);
        }
    };

    // Loading state
    if (cartLoading) {
        return (
            <div className="min-h-screen bg-muted pt-[10px]">
                <div className="max-w-[1400px] mx-auto px-4 py-6">
                    <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-muted-foreground">Loading your cart...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const items = cart?.items || [];
    const summary = cart?.summary || {};
    const cartIssues = cart?.issues || [];
    const isEmpty = items.length === 0;

    // Empty cart state
    if (isEmpty) {
        return (
            <div className="min-h-screen bg-muted pt-[10px]">
                <div className="max-w-[1400px] mx-auto px-4 py-6">
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-5">
                            <i className="ri-shopping-cart-line text-4xl text-muted-foreground/40"></i>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-2">Your cart is empty</h2>
                        <p className="text-muted-foreground mb-5">Looks like you haven't added anything to your cart yet.</p>
                        <button
                            onClick={() => navigate('/products')}
                            className="px-8 py-2.5 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all"
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="min-h-screen bg-muted pt-[10px]">
            <div className="max-w-[1400px] mx-auto px-4 py-6">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm mb-4 flex-wrap">
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <span className="text-foreground font-medium">Shopping Cart</span>
                </nav>

                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-xl font-bold text-foreground">
                        Shopping Cart
                        <span className="text-sm font-normal text-muted-foreground ml-2">({cart?.itemCount || 0} items)</span>
                    </h1>
                    <button
                        onClick={handleClearCart}
                        disabled={clearing}
                        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                        {clearing ? (
                            <div className="w-4 h-4 border-2 border-border border-t-red-500 rounded-full animate-spin"></div>
                        ) : (
                            <i className="ri-delete-bin-line"></i>
                        )}
                        Clear Cart
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-3">
                        {items.map((item) => {
                            const vid = variantKey(item.variantId);
                            const maxQty = 10;
                            const itemIssue = cartIssues.find(i => variantKey(i.variantId) === vid);
                            const image = getProductImage(item);

                            return (
                                <div
                                    key={vid || item._id}
                                    className={`bg-card rounded-lg border p-3 transition-all ${itemIssue ? 'border-amber-300 bg-amber-50/30' : 'border-border'
                                        }`}
                                >
                                    <div className="flex gap-3">
                                        {/* Image */}
                                        <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-muted rounded-lg overflow-hidden">
                                            {image ? (
                                                <img
                                                    src={image}
                                                    alt={item.product?.name || 'Product'}
                                                    className="w-full h-full object-contain p-2"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <i className="ri-image-line text-3xl text-muted-foreground/40"></i>
                                                </div>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <Link
                                                        to={`/product/${item.product?.slug}`}
                                                        className="text-sm sm:text-base font-medium text-foreground hover:text-primary transition-colors line-clamp-2"
                                                    >
                                                        {item.product?.name}
                                                    </Link>

                                                    {/* Variant info */}
                                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                                        {item.variant?.colorId?.name && (
                                                            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                                                {item.variant.colorId.name}
                                                            </span>
                                                        )}
                                                        {(item.variant?.variantAttributes || []).map((attr, idx) => (
                                                            <span key={idx} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                                                {attr.attributeId?.name}: {attr.valueId?.name}
                                                            </span>
                                                        ))}
                                                    </div>

                                                    {/* Offer badge */}
                                                    {item.pricing?.offer && (
                                                        <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded">
                                                            <i className="ri-price-tag-3-line text-xs"></i>
                                                            {item.pricing.offer.name}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Remove button */}
                                                <button
                                                    onClick={() => handleRemoveItem(vid)}
                                                    disabled={removingItems[vid]}
                                                    className="flex-shrink-0 p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                    title="Remove item"
                                                >
                                                    {removingItems[vid] ? (
                                                        <div className="w-4 h-4 border-2 border-border border-t-red-500 rounded-full animate-spin"></div>
                                                    ) : (
                                                        <i className="ri-delete-bin-line text-lg"></i>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Issues */}
                                            {itemIssue && (
                                                <div className="mt-2">
                                                    <p className="text-xs text-amber-700 flex items-center gap-1">
                                                        <i className="ri-error-warning-line"></i>
                                                        {itemIssue.issue}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Price & Quantity */}
                                            <div className="flex items-end justify-between mt-2.5">
                                                {/* Price */}
                                                <div>
                                                    {item.pricing?.discountAmount > 0 ? (
                                                        <div className="flex items-baseline gap-2">
                                                            <span className="text-sm font-bold text-foreground">
                                                                {formatKES(item.pricing?.discountedPrice)}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground line-through">
                                                                {formatKES(item.pricing?.unitPrice)}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm font-bold text-foreground">
                                                            {formatKES(item.pricing?.unitPrice)}
                                                        </span>
                                                    )}
                                                    {item.quantity > 1 && (
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                            Total: {formatKES(item.pricing?.lineTotal)}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Quantity controls */}
                                                <div className="flex items-center gap-0 border border-border rounded-lg overflow-hidden">
                                                    <button
                                                        onClick={() => handleUpdateQuantity(vid, item.quantity - 1)}
                                                        disabled={item.quantity <= 1 || updatingItems[vid]}
                                                        className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                    >
                                                        <i className="ri-subtract-line text-sm"></i>
                                                    </button>
                                                    <span className="w-9 h-7 flex items-center justify-center text-sm font-medium text-foreground border-x border-border">
                                                        {updatingItems[vid] ? (
                                                            <div className="w-3 h-3 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                                                        ) : (
                                                            item.quantity
                                                        )}
                                                    </span>
                                                    <button
                                                        onClick={() => handleUpdateQuantity(vid, item.quantity + 1)}
                                                        disabled={item.quantity >= maxQty || updatingItems[vid]}
                                                        className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                    >
                                                        <i className="ri-add-line text-sm"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Free Gift Progress */}
                        {cart?.giftEvaluation && (
                            <CartGiftProgress
                                giftEvaluation={cart.giftEvaluation}
                                onOpenGiftModal={(slab) => setGiftModalSlab(slab)}
                                onGiftRemoved={() => refreshCart?.()}
                            />
                        )}

                        {/* Recommended compatible accessories */}
                        {recommendations.length > 0 && (
                            <CompatibleAccessories
                                items={recommendations}
                                onAddToCart={addToCart}
                                title="Add compatible accessories"
                            />
                        )}

                        {/* Continue shopping */}
                        <div className="pt-2">
                            <button
                                onClick={() => navigate('/products')}
                                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Continue Shopping
                            </button>
                        </div>
                    </div>

                    {/* Cart Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-card rounded-lg border border-border p-5 lg:sticky lg:top-[180px]">
                            <h2 className="text-base font-bold text-foreground mb-3">Order Summary</h2>

                            <div className="space-y-2.5 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Subtotal</span>
                                    <span className="text-foreground font-medium">{formatKES(summary.subtotal)}</span>
                                </div>

                                {summary.couponDiscount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-green-600">Discount ({cart.couponCode})</span>
                                        <span className="text-green-600 font-medium">-{formatKES(summary.couponDiscount)}</span>
                                    </div>
                                )}

                                {(summary.vatAddedAmount ?? 0) > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">VAT ({summary.vatRate ?? vatRate}%) on eligible items</span>
                                        <span className="text-foreground font-medium">
                                          {formatKES(summary.vatAddedAmount)}
                                        </span>
                                    </div>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Shipping</span>
                                    <span className={`font-medium ${summary.shippingCharge === 0 ? 'text-green-600' : 'text-foreground'}`}>
                                        {summary.shippingCharge === 0 ? 'Free' : formatKES(summary.shippingCharge)}
                                    </span>
                                </div>

                                <div className="pt-1">
                                    <CouponInput />
                                </div>

                                <div className="border-t border-border pt-2.5 mt-2.5">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-bold text-foreground">Grand Total</span>
                                        <span className="text-base font-bold text-primary">{formatKES(summary.total)}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/checkout')}
                                disabled={cart?.hasIssues}
                                className="w-full mt-5 py-2.5 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                Proceed to Checkout
                                <i className="ri-arrow-right-line"></i>
                            </button>

                            {cart?.hasIssues && (
                                <p className="mt-2.5 text-xs text-amber-600 text-center flex items-center justify-center gap-1">
                                    <i className="ri-error-warning-line"></i>
                                    Please resolve cart issues before proceeding
                                </p>
                            )}

                            <button
                                onClick={() => setBulkModalOpen(true)}
                                className="w-full mt-2.5 py-2.5 border-2 border-border text-muted-foreground rounded-lg font-medium hover:border-gray-900 hover:text-gray-900 transition-colors flex items-center justify-center gap-2 text-sm"
                            >
                                <i className="ri-briefcase-line"></i>
                                Request Bulk / Corporate Quote
                            </button>

                            {!isAuthenticated && (
                                <div className="mt-3 p-2.5 bg-muted rounded-lg">
                                    <p className="text-xs text-muted-foreground text-center">
                                        You can checkout as guest or{' '}
                                        <Link to="/login" className="text-primary font-medium hover:underline">
                                            sign in
                                        </Link>{' '}
                                        for a better experience
                                    </p>
                                </div>
                            )}

                            {/* Trust signals */}
                            <div className="mt-5 pt-3 border-t border-border space-y-2">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <i className="ri-shield-check-line text-green-600"></i>
                                    Secure checkout
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <i className="ri-truck-line text-green-600"></i>
                                    Fast delivery across Kenya
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <i className="ri-refund-2-line text-green-600"></i>
                                    Easy returns
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {bulkModalOpen && (
            <BulkInquiryModal
                isOpen={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                prefillItems={(cart?.items || []).map((item) => ({
                    productId: item.productId,
                    variantId: item.variantId,
                    productName: item.product?.name || '',
                    sku: item.variant?.sku || '',
                    imageUrl: getProductImage(item) || '',
                    quantity: item.quantity,
                    unitPrice: item.pricing?.discountedPrice || item.pricing?.unitPrice || 0,
                }))}
                source="cart"
            />
        )}

        {giftModalSlab && (
            <GiftSelectionModal
                currentSlab={giftModalSlab}
                selectedGifts={cart?.giftEvaluation?.selectedGifts || []}
                onClose={() => setGiftModalSlab(null)}
                onGiftSelected={() => { setGiftModalSlab(null); refreshCart?.(); }}
            />
        )}
        </>
    );
}
